import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { API_BASE } from '../config/api'
import { useTheme } from '../hooks/useTheme'

interface FooterContent {
  companyDescription: string
  quickLinks: Array<{ name: string; href: string }>
  services: string[]
  copyrightText: string
}

interface SocialSettings {
  github?: string
  linkedin?: string
  twitter?: string
  instagram?: string
  youtube?: string
}

// Default fallback content
const defaultFooterContent: FooterContent = {
  companyDescription: 'A passionate team of developers, designers, and innovators dedicated to creating stunning digital experiences that push the boundaries of what\'s possible.',
  quickLinks: [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Team', href: '/team' },
    { name: 'Projects', href: '/projects' },
  ],
  services: [
    'Mobile App Development',
    'Web Development',
    'API Development',
    'Backend Development',
  ],
  copyrightText: 'Ionic Errrrs Code. All rights reserved.'
}

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const [footerContent, setFooterContent] = useState<FooterContent>(defaultFooterContent)
  const [socialLinks, setSocialLinks] = useState<SocialSettings | null>(null)
  const [contactEmail, setContactEmail] = useState('hello@ionicerrrrscode.com')
  const { theme } = useTheme()

  // Fetch footer content from API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [footerRes, socialRes, contactRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/content/footer`),
          fetch(`${API_BASE}/api/v1/settings/social`),
          fetch(`${API_BASE}/api/v1/settings/contact`)
        ])

        const footerData = await footerRes.json()
        const socialData = await socialRes.json()
        const contactData = await contactRes.json()

        if (footerData.success && footerData.data) {
          setFooterContent(footerData.data)
        }
        if (socialData.success && socialData.data) {
          setSocialLinks(socialData.data)
        }
        if (contactData.success && contactData.data?.email) {
          setContactEmail(contactData.data.email)
        }
      } catch (error) {
        console.error('Failed to fetch footer content:', error)
      }
    }
    fetchContent()
  }, [])

  const getSocialLabel = (key: string) => {
    if (key.toLowerCase() === 'github') return 'GitHub'
    if (key.toLowerCase() === 'linkedin') return 'LinkedIn'
    if (key.toLowerCase() === 'youtube') return 'YouTube'
    return key.charAt(0).toUpperCase() + key.slice(1)
  }

  const socialEntries = socialLinks
    ? Object.entries(socialLinks)
        .filter(([, url]) => url && url.trim())
        .map(([key, url]) => ({
          label: getSocialLabel(key),
          href: url as string,
        }))
    : [
        { label: 'GitHub', href: 'https://github.com/Ionic-Errrrs-Code' },
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/ionicerrrrscode/' },
      ]

  return (
    <footer style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
      <div className="container-page" style={{ paddingTop: '64px', paddingBottom: '40px' }}>
        {/* Main grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-10">
          {/* Brand column */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 no-underline mb-5">
              <img 
                src={theme === 'dark' ? '/logo-dark.png' : '/logo-light.png'} 
                alt="Ionic Errrrs Code Logo" 
                className="w-[30px] h-[30px] object-contain rounded-md" 
              />
              <span className="font-display text-[1.12rem] font-semibold" style={{ color: 'var(--text)', letterSpacing: '-.02em' }}>
                Ionic Errrrs Code
              </span>
            </Link>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)', maxWidth: '280px' }}>
              {footerContent.companyDescription}
            </p>
          </div>

          {/* Services column */}
          <div>
            <h4 className="font-display text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Services</h4>
            <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
              {footerContent.services.map((service) => (
                <li key={service}>
                  <span className="text-sm transition-colors duration-300 cursor-default" style={{ color: 'var(--muted)' }}>
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Company column */}
          <div>
            <h4 className="font-display text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Company</h4>
            <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
              {footerContent.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={(e) => {
                      if (link.href === '/' && window.location.pathname === '/') {
                        e.preventDefault()
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }
                    }}
                    className="text-sm no-underline transition-colors duration-300"
                    style={{ color: 'var(--muted)' }}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect column */}
          <div>
            <h4 className="font-display text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Connect</h4>
            <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
              {socialEntries.map((social) => (
                <li key={social.label}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm no-underline transition-colors duration-300"
                    style={{ color: 'var(--muted)' }}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}
                  >
                    {social.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-sm no-underline transition-colors duration-300"
                  style={{ color: 'var(--muted)' }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}
                >
                  Email
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="flex flex-wrap justify-between items-center gap-4 mt-10 pt-6"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            © {currentYear} {footerContent.copyrightText}
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-sm no-underline transition-colors duration-300" style={{ color: 'var(--muted)' }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}>
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm no-underline transition-colors duration-300" style={{ color: 'var(--muted)' }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}>
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
